import React, { useCallback } from 'react';
import { TableCell, TableRow } from '../../../../components/Table/Table';
import { Input } from '../../../../components/Form';
import ReactSelect from '../../../../components/ReactSelect';
import type {
  PlatformProduct,
  PlatformProductInput,
  ProductType,
} from './planProduct.types';
import { productTypeLabels } from './planProduct.types';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import { UtilityIconButton } from '../../../../components/Button/UtilityIconButton';

type ProductRowProps = {
  product: PlatformProduct | PlatformProductInput;
  onChange: (product: PlatformProduct | PlatformProductInput) => void;
  onRemove: () => void;
};

// Base frequency options
const baseFrequencyOptions = [
  { value: 'IMMEDIATE' as const, label: 'Immediate' },
  { value: 'DAILY' as const, label: 'Daily' },
  { value: 'WEEKLY' as const, label: 'Weekly' },
  { value: 'MONTHLY' as const, label: 'Monthly' },
  { value: 'ANNUALLY' as const, label: 'Annually' },
];

// Base timing options
const baseTimingOptions = [
  { value: 'IN_ADVANCE' as const, label: 'In Advance' },
  { value: 'IN_ARREARS' as const, label: 'In Arrears' },
];

const isConsumptionBased = (productType: ProductType) =>
  [
    'BACKFLOW_TEST_PASSED',
    'BACKFLOW_TEST',
    'BACKFLOW_NOTICE_CREATED',
    'BACKFLOW_NOTICE_MAILED',
  ].includes(productType);

// Get frequency options based on product type
const getFrequencyOptions = (productType: ProductType) => {
  if (productType === 'BACKFLOW_ACTIVE_ASSEMBLY') {
    return baseFrequencyOptions.filter((opt) => opt.value === 'ANNUALLY');
  }
  // if (productType === 'SUBSCRIPTION') {
  //   return baseFrequencyOptions.filter(
  //     (opt) => opt.value === 'MONTHLY' || opt.value === 'ANNUALLY'
  //   );
  // }
  if (isConsumptionBased(productType)) {
    return baseFrequencyOptions.filter((opt) => opt.value !== 'ANNUALLY');
  }
  return [...baseFrequencyOptions];
};

// Get timing options based on product type
const getTimingOptions = (productType: ProductType) => {
  // if (productType === 'SUBSCRIPTION') {
  //   return baseTimingOptions.filter((opt) => opt.value === 'IN_ADVANCE');
  // }
  if (isConsumptionBased(productType)) {
    return baseTimingOptions.filter((opt) => opt.value === 'IN_ARREARS');
  }
  return [...baseTimingOptions];
};

const billToOptions = [
  { value: 'WATER_SUPPLIER' as const, label: 'Water Supplier' },
  { value: 'TESTER' as const, label: 'Tester' },
];

export const ProductRow: React.FC<ProductRowProps> = ({
  product,
  onChange,
  onRemove,
}) => {
  const handleChange = useCallback(
    (field: keyof PlatformProduct) => (value: any) => {
      const newValue = value?.value ?? value;
      const updatedProduct = { ...product, [field]: newValue };

      // If product type changed, reset dependent fields to valid values
      if (field === 'type') {
        const newType = newValue as ProductType;
        const frequencyOptions = getFrequencyOptions(newType);
        const timingOptions = getTimingOptions(newType);

        // Set default frequency based on product type
        if (isConsumptionBased(newType)) {
          updatedProduct.billingFrequency = 'MONTHLY';
        }
        // Reset frequency if current value is not valid for new type
        else if (
          !frequencyOptions.some(
            (opt) => opt.value === product.billingFrequency
          )
        ) {
          updatedProduct.billingFrequency = frequencyOptions[0].value;
        }

        // Reset timing if current value is not valid for new type
        if (!timingOptions.some((opt) => opt.value === product.billingTiming)) {
          updatedProduct.billingTiming = timingOptions[0].value;
        }
      }

      onChange(updatedProduct);
    },
    [product, onChange]
  );

  // Get current options based on selected product type
  const currentFrequencyOptions = getFrequencyOptions(product.type);
  const currentTimingOptions = getTimingOptions(product.type);

  const getValue = (value: string) => {
    if (!value) return null;
    if (value in productTypeLabels) {
      return { value, label: productTypeLabels[value as ProductType] };
    }
    return { value, label: constantToLabel(value) };
  };

  const productTypeOptions = [
    {
      label: 'Backflow',
      options: Object.entries(productTypeLabels)
        .filter(([key]) => key.startsWith('BACKFLOW_'))
        .map(([value, label]) => ({
          value: value as ProductType,
          label,
        })),
    },
    {
      label: 'Other',
      options: Object.entries(productTypeLabels)
        .filter(([key]) => !key.startsWith('BACKFLOW_'))
        .map(([value, label]) => ({
          value: value as ProductType,
          label,
        })),
    },
  ];

  return (
    <TableRow
      style={{
        display: 'grid',
        gridTemplateColumns: '220px 110px 180px 150px 150px 60px',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <TableCell>
        <ReactSelect
          value={getValue(product.type)}
          onChange={(option) => handleChange('type')(option?.value)}
          options={productTypeOptions}
          isSearchable={false}
          isDisabled={productTypeOptions.length < 2}
          styles={{
            menu: (base) => ({
              ...base,
              minWidth: '220px',
            }),
          }}
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          value={product.price}
          onChange={(e) =>
            handleChange('price')(parseFloat(e.target.value) || 0)
          }
          placeholder="0.00"
          min="0"
          step="0.01"
          sx={{
            width: '100%',
          }}
        />
      </TableCell>
      <TableCell>
        <ReactSelect
          value={getValue(product.billTo)}
          onChange={(option) => handleChange('billTo')(option?.value)}
          options={billToOptions}
          isDisabled={billToOptions.length < 2}
          isSearchable={false}
        />
      </TableCell>
      <TableCell>
        <ReactSelect
          value={getValue(product.billingFrequency)}
          onChange={(option) => handleChange('billingFrequency')(option?.value)}
          options={currentFrequencyOptions}
          isDisabled={currentFrequencyOptions.length < 2}
          isSearchable={false}
        />
      </TableCell>
      <TableCell>
        <ReactSelect
          value={getValue(product.billingTiming)}
          onChange={(option) => handleChange('billingTiming')(option?.value)}
          options={currentTimingOptions}
          isDisabled={currentTimingOptions.length < 2}
          isSearchable={false}
        />
      </TableCell>
      <TableCell className="product-table-cell" style={{ textAlign: 'center' }}>
        <UtilityIconButton dark icon="trash" onClick={onRemove}>
          Remove
        </UtilityIconButton>
      </TableCell>
    </TableRow>
  );
};
